import React, { useEffect, useState } from 'react';
import { ErrorMessage } from '@atlaskit/form';
import InlineEdit from '@atlaskit/inline-edit';
import Spinner from '@atlaskit/spinner';
import Textfield from '@atlaskit/textfield';
import { view } from '@forge/bridge';

import licenseApi from '../../../api/licenseApi';
import fieldForgeApi, { FieldConfiguration } from '../../../bridge/fieldForgeApi';
import { Label,SelectDescription } from '../../../styles';
import { LicenseBanner } from '../../../utils/LicenseBanner';

import { Wrapper, WrapperRegex } from './indexStyled';

type RegexConfigModel = {
    error: string;
    regex: string;
};

type Props = {
    fieldDescription?: string;
    fieldId?: string;
    fieldName?: string;
    onSaveOverride?: (model: string, isValid: boolean) => Promise<void>;
};

const ISSUE_VIEW_RENDER_CONTEXT = 'issue-view';
const ISSUE_CREATE_RENDER_CONTEXT = 'issue-create';
const ISSUE_CREATE_PORTAL_RENDER_CONTEXT = 'portal-create';
const ISSUE_TRANSITION_RENDER_CONTEXT = 'issue-transition';

const Edit: React.FC<Props> = ({ fieldDescription, fieldName, fieldId, onSaveOverride }) => {
    const [regex, setRegex] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [fieldValue, setFieldValue] = useState('');
    const [renderContext, setRenderContext] = useState('');
    const [isRegexValid, setIsRegexValid] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [isLicenseActive, setIsLicenseActive] = useState(false);
    const [initialFieldValue, setInitialFieldValue] = useState('');

    const isIssueViewContext = renderContext === ISSUE_VIEW_RENDER_CONTEXT;
    const isGicCreateContext = renderContext === ISSUE_CREATE_RENDER_CONTEXT;
    const isPortalCreateContext = renderContext === ISSUE_CREATE_PORTAL_RENDER_CONTEXT;
    const isIssueTransitionContext = renderContext === ISSUE_TRANSITION_RENDER_CONTEXT;

    const handleFieldValueChange = async (value: string) => {
        setFieldValue(value);
        setIsRegexValid(new RegExp(regex).test(value));

        if (isLicenseActive && (isGicCreateContext || isPortalCreateContext || isIssueTransitionContext)) {
            const isRegexValid = value ? new RegExp(regex).test(value) : true;
            (isPortalCreateContext || isIssueTransitionContext) && onSaveOverride ?
                await onSaveOverride(value, isRegexValid)
            :   await view.submit(value);
        }
    };

    const handleFieldConfigurationSetup = (config: FieldConfiguration) => {
        if (config && config?._configuration) {
            const configuration = config._configuration as RegexConfigModel;
            setRegex(configuration.regex);
            setErrorMessage(configuration.error != '' ? configuration.error : 'Enter a value that will be validated for the configured regular expression: ' + configuration.regex);
        }
    };

    const validate = (value: string) => {
        if (regex && value.length >= 1 && !isRegexValid) {
            return errorMessage;
        }
        return undefined;
    };

    const handleCancel = async () => {
        setFieldValue(initialFieldValue);
        await view.close();
    };

    const handleSubmit = async () => {
        setInitialFieldValue(fieldValue);
        await view.submit(fieldValue);
    };

    useEffect(() => {
        const getFieldConfig = async () => {
            try {
                const context = await view.getContext();
                const [isLicenseActive, fieldConfiguration]: [isActiveValue: boolean, fieldConfiguration: FieldConfiguration] =
                    await Promise.all([
                        licenseApi.getIsLicenseActive(),
                        fieldForgeApi.getFieldConfiguration((context.extension.fieldId || fieldId) as string),
                    ]);
                setRenderContext((context?.extension?.renderContext || ISSUE_CREATE_PORTAL_RENDER_CONTEXT) as string);
                setFieldValue((context?.extension?.fieldValue as string) || '');
                setInitialFieldValue((context?.extension?.fieldValue as string) || '');
                setIsLicenseActive(isLicenseActive);
                handleFieldConfigurationSetup(fieldConfiguration);
            } catch (e) {
                console.error(e);
            } finally {
                setIsLoading(false);
            }
        };
        void getFieldConfig();
    }, []);

    if (isLoading) {
        return (
            <div>
                <Spinner size='medium' />
            </div>
        );
    }
    return (
        <LicenseBanner isLicenseValid={isLicenseActive}>
                <div>
                    {isPortalCreateContext && <Label bottomMargin={'4px'}>{fieldName}</Label>}
                    {(isGicCreateContext || isPortalCreateContext || isIssueTransitionContext) &&
                        <Textfield
                            value={fieldValue}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => void handleFieldValueChange(e.target.value)}
                            isDisabled={!isLicenseActive}
                        />}
                    { isIssueViewContext && <InlineEdit
                        label={fieldName}
                        defaultValue={fieldValue}
                        editView={({ errorMessage, ...fieldProps }) => (
                                <WrapperRegex>
                                    <Textfield
                                        {...fieldProps}
                                        value={fieldValue}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            void handleFieldValueChange(e.target.value)
                                        }
                                        isDisabled={!isLicenseActive}
                                        isInvalid={!isRegexValid}
                                        autoFocus
                                    />
                                </WrapperRegex>
                        )}
                        readView={() => (
                            <Textfield
                                isDisabled={!isLicenseActive}
                                value={fieldValue}
                            />
                        )}
                        onConfirm={handleSubmit}
                        onCancel={handleCancel}
                        startWithEditViewOpen
                        readViewFitContainerWidth
                        hideActionButtons
                        validate={validate}
                    />}
                    {(isIssueViewContext || isPortalCreateContext) && regex && fieldValue.length >= 1 ? !isRegexValid && <ErrorMessage>{errorMessage}</ErrorMessage> : null}
                    {fieldDescription && <SelectDescription>{fieldDescription}</SelectDescription>}
                </div>
        </LicenseBanner>
    );
};

export default Edit;
